import {
  APIFieldAttributes,
  APIWorkspaceAttributes,
  ArdoqId,
} from '@ardoq/api-types';
import { isEqual } from '@ardoq/common-helpers';
import { spreadMap } from '@ardoq/rxbeach';
import { combineLatest } from 'rxjs';
import { distinctUntilChanged, shareReplay } from 'rxjs/operators';
import { fields$ } from 'streams/fields/fields$';
import { StreamCollection } from 'streams/utils/streamUtils';
import { persistentWorkspaces$ as workspaces$ } from 'streams/workspaces/workspaces$';

export type FieldsByWorkspaceId = Readonly<
  Record<ArdoqId, readonly APIFieldAttributes[]>
>;

/**
 * Build a `workspaceId → workspace fields` lookup, mirroring what
 * `fieldInterface.getAllFieldsOfWorkspace(id)` returns.
 *
 * Workspaces sharing the same `componentModel` reference the same array
 * instance — the `readonly` return type forbids in-place mutation; callers
 * that need to sort/filter should clone first (e.g. `[...arr].sort(...)`).
 */
const buildFieldsByWorkspaceId = (
  fieldsState: StreamCollection<APIFieldAttributes>,
  workspacesState: StreamCollection<APIWorkspaceAttributes>
): FieldsByWorkspaceId => {
  const fieldsByModelId = new Map<ArdoqId, APIFieldAttributes[]>();
  fieldsState.models.forEach(field => {
    const list = fieldsByModelId.get(field.model);
    if (list) {
      list.push(field);
    } else {
      fieldsByModelId.set(field.model, [field]);
    }
  });

  const fieldsByWorkspaceId: Record<ArdoqId, APIFieldAttributes[]> = {};
  workspacesState.models.forEach(ws => {
    fieldsByWorkspaceId[ws._id] = fieldsByModelId.get(ws.componentModel) ?? [];
  });
  return fieldsByWorkspaceId;
};

export const fieldsByWorkspaceId$ = combineLatest([fields$, workspaces$]).pipe(
  spreadMap(buildFieldsByWorkspaceId),
  distinctUntilChanged(isEqual),
  shareReplay({ bufferSize: 1, refCount: true })
);
